import { DatePicker, DatePickerInput } from "@carbon/react";
import ClassHeader from "../Components/classHeader";
import Header from "../Components/header";
import TeacherHeader from "../Components/teacherHeader";
import DayTimetable from "../Components/dayTimetable";
import { Route, Routes, useSearchParams } from "react-router-dom";
import TeacherToday from "../Pages/Teacher/TeacherToday";
import TeacherDashboard from "../Pages/Teacher/TeacherDashboard";
import { gql, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import TeacherClassStats from "../Pages/Teacher/TeacherClassStats";
import TeacherStudentEdit from "../Pages/Teacher/TeacherStudentEdit";
import TeacherClassEdit from "../Pages/Teacher/TeacherClassEdit";
import Footer from "../Components/Footer";

// GraphQL query to fetch all classes so we can validate selectedClass
const GET_ALL_GROUPS = gql`
  query GetAllGroups {
    allGroups {
      __typename
      id
      name
    }
  }
`;

export function TeacherLayout(){

    const [searchParams, setSearchParams] = useSearchParams();
    const getInitialClass = () => {
        const urlClass = searchParams.get('class');
        if (urlClass) return urlClass;
        try {
            const stored = localStorage.getItem('selectedClass');
            return stored || null;
        } catch (e) {
            return null;
        }
    };
    const [selectedClass, setSelectedClass] = useState(getInitialClass);

    // Fetch all groups (including SchoolClass) so we can detect if the stored/URL selection exists
    const { loading: groupsLoading, data: groupsData } = useQuery(GET_ALL_GROUPS, {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'ignore'
    });

    // If the currently selectedClass (from URL/localStorage) does not exist on the server,
    // automatically fall back to the first available SchoolClass (if possible) or any group when selection is invalid
    useEffect(() => {
        if (groupsLoading) return;
        const groups = groupsData?.allGroups || [];
        if (!groups.length) return;

        const exists = selectedClass && groups.some(g => g.id === selectedClass);
        if (!exists) {
            // prefer a real SchoolClass as fallback
            const firstClass = groups.find(g => g.__typename === 'SchoolClass') || groups[0];
            setSelectedClass(firstClass.id);
        }
    }, [groupsLoading, groupsData, selectedClass]);

    // Update state when user selects a class (do not directly touch URL/localStorage here)
    const handleClassChange = (classId) => {
        setSelectedClass(classId);
    };

    // Sync selectedClass -> URL and localStorage
    useEffect(() => {
        const currentUrlClass = searchParams.get('class');
        if (selectedClass) {
            // only update URL if it differs to avoid loops
            if (currentUrlClass !== selectedClass) {
                setSearchParams({ class: selectedClass });
            }
            try { localStorage.setItem('selectedClass', selectedClass); } catch (e) {}
        } else {
            if (currentUrlClass !== null) {
                setSearchParams({});
            }
            try { localStorage.removeItem('selectedClass'); } catch (e) {}
        }
    }, [selectedClass]);

    // Update state when URL changes (e.g., browser back/forward)
    useEffect(() => {
        const classFromUrl = searchParams.get('class');
        const normalized = classFromUrl || null;
        if (normalized !== selectedClass) {
            setSelectedClass(normalized);
        }
    }, [searchParams]);

    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <TeacherHeader setSelectedClass={handleClassChange} selectedClass={selectedClass} />
            <ClassHeader selectedClass={selectedClass} />
            <section className="flex-1 container px-4 xlg:px-0 mx-auto">
                <Routes>
                    <Route path="/" element={<TeacherDashboard selectedClass={selectedClass} />} />
                    <Route path="/today" element={<TeacherToday selectedClass={selectedClass}  />} />
                    <Route path="/stats" element={<TeacherClassStats selectedClass={selectedClass}  />} />
                    <Route path="/students" element={<TeacherStudentEdit selectedClass={selectedClass} />} />
                    <Route path="/editclass" element={<TeacherClassEdit selectedClass={selectedClass} />} />
                </Routes>
            </section>
            <Footer />
        </div>
    );
}